import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { TFunction } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import {
  checkRegistration,
  connectToNewClient,
  ConnectToNewClientErrorType,
  signUp,
} from "@/api/authentication/signUp.ts";
import { login } from "@/api/common/interceptors/authenticationInterceptor.ts";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";
import { useClient } from "@/hooks/useClient.ts";
import { Link } from "react-router";
import { Trans } from "react-i18next";
import EnhancedDatePicker from "@/components/EnhancedDataPicker.tsx";
import WheelDatePicker from "@/components/WheelDatePicker";
import { isDesktop } from "react-device-detect";
import PasswordInput from "@/components/PasswordInput.tsx";
import { setTitle } from "@/hooks/useHeader.ts";
import QuizForm from "@/components/QuizForm.tsx";
import { useTranslation } from "@/i18n";
import { getSignupQuestion } from "@/lib/signupQuestion.ts";
import SignupQuestionSelect from "@/components/SignupQuestionSelect.tsx";

const createSignupAnswerSchema = (t: TFunction, required: boolean) =>
  required
    ? z.string().min(1, t("validation.signupAnswerRequired"))
    : z.string();

const createEmailFormSchema = (t: TFunction) =>
  z.object({
    email: z.string().email({ message: t("validation.invalidEmail") }),
  });

type EmailFormValues = z.infer<ReturnType<typeof createEmailFormSchema>>;

const createConnectFormSchema = (t: TFunction, signupAnswerRequired: boolean) =>
  z.object({
    password: z.string().min(1, t("validation.passwordRequired")),
    signupAnswer: createSignupAnswerSchema(t, signupAnswerRequired),
  });

type ConnectFormValues = z.infer<ReturnType<typeof createConnectFormSchema>>;

const createSignUpFormSchema = (t: TFunction, signupAnswerRequired: boolean) =>
  z
    .object({
      clubCode: z.string(),
      name: z.string().min(1, t("validation.nameRequired")),
      phoneNumber: z.string().min(10, t("validation.phoneNumberRequired")),
      dateOfBirth: z
        .date({
          required_error: t("validation.dateOfBirthRequired"),
        })
        .refine(
          (date) => {
            const minAge = new Date();
            minAge.setFullYear(minAge.getFullYear() - 18);
            return date < new Date(minAge);
          },
          {
            message: t("validation.minimumAge"),
          },
        ),
      password: z.string().min(8, t("validation.passwordMinLength")),
      confirmPassword: z.string().min(8, t("validation.passwordMinLength")),
      passcode: z.string(),
      signupAnswer: createSignupAnswerSchema(t, signupAnswerRequired),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("validation.passwordsDoNotMatch"),
      path: ["confirmPassword"],
    });

type SignUpFormValues = z.infer<ReturnType<typeof createSignUpFormSchema>>;

const Signup = ({ className, ...props }: ComponentProps<"div">) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const client = useClient();
  const { t } = useTranslation();
  const signupQuestion = getSignupQuestion(client);
  const signupAnswerRequired = Boolean(signupQuestion?.required);
  const emailFormSchema = useMemo(() => createEmailFormSchema(t), [t]);
  const connectFormSchema = useMemo(
    () => createConnectFormSchema(t, signupAnswerRequired),
    [t, signupAnswerRequired],
  );
  const signUpFormSchema = useMemo(
    () => createSignUpFormSchema(t, signupAnswerRequired),
    [t, signupAnswerRequired],
  );

  useEffect(() => {
    setTitle(t("auth.signUp.title"));
  }, [t]);

  const [currentForm, setCurrentForm] = useState<
    "email" | "connect" | "signUp" | "success" | "quiz"
  >("quiz");

  // ----------------------Email-----------------------------
  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmitEmailForm = async (values: EmailFormValues) => {
    setLoading(true);
    setError("");
    try {
      const response = await checkRegistration(values);
      if (response.data.status === "already-exist") {
        emailForm.setError("email", {
          type: "custom",
          message: t("auth.signUp.userAlreadyExists"),
        });
      } else if (response.data.status === "already-exist-on-another-client") {
        setCurrentForm("connect");
      } else {
        setCurrentForm("signUp");
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      setError(t("common.somethingWentWrongTryAgain"));
    }
    setLoading(false);
  };

  const EmailFrom = (
    <Form {...emailForm}>
      <form onSubmit={emailForm.handleSubmit(onSubmitEmailForm)}>
        <div className="grid gap-6">
          <div className="grid gap-6">
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.email")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("form.emailPlaceholder")}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setError(undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {t("auth.signUp.continue")}{" "}
              {loading && <LoaderCircle className="animate-spin" />}
            </Button>
          </div>
          <div className="text-center text-sm">
            {t("auth.signUp.alreadyHaveAccount")}{" "}
            <Link to="/sign-in" className="underline underline-offset-4">
              {t("auth.signUp.signInLink")}
            </Link>
          </div>
        </div>
      </form>
    </Form>
  );

  // ----------------------Connect-----------------------------
  const connectForm = useForm<ConnectFormValues>({
    resolver: zodResolver(connectFormSchema),
    defaultValues: {
      password: "",
      signupAnswer: "",
    },
  });

  const onSubmitConnectForm = async (values: ConnectFormValues) => {
    setLoading(true);
    setError("");
    try {
      const response = await connectToNewClient({
        password: values.password,
        email: emailForm.getValues("email"),
        signupAnswer: signupQuestion ? values.signupAnswer || null : undefined,
      });
      await login({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      });
    } catch (e: unknown) {
      const response = getErrorResponse<ConnectToNewClientErrorType>(e);
      let errorMessage = t("common.somethingWentWrongTryAgain");
      if (response) {
        if ("signup_answer" in response) {
          connectForm.setError("signupAnswer", {
            type: "server",
            message: response.signup_answer[0],
          });
          setLoading(false);
          return;
        } else if ("detail" in response) {
          errorMessage = response.detail;
        } else if ("user" in response) {
          errorMessage = response.user;
        } else if ("email" in response) {
          errorMessage = response.email;
        }
      }
      setError(errorMessage);
    }
    setLoading(false);
  };

  const ConnectFrom = (
    <Form {...connectForm}>
      <form onSubmit={connectForm.handleSubmit(onSubmitConnectForm)}>
        <div className="grid gap-6">
          <FormField
            control={connectForm.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="password">{t("form.password")}</FormLabel>
                <FormControl>
                  <PasswordInput
                    id="password"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setError(undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {signupQuestion && (
            <FormField
              control={connectForm.control}
              name="signupAnswer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {signupQuestion.label}
                    {signupQuestion.required && " *"}
                  </FormLabel>
                  <SignupQuestionSelect
                    question={signupQuestion}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setError(undefined);
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <Button type="submit" className="w-full" disabled={loading}>
            {t("auth.signUp.connectAccount")}{" "}
            {loading && <LoaderCircle className="animate-spin" />}
          </Button>
        </div>
      </form>
    </Form>
  );

  // ----------------------SignUp-----------------------------
  const signUpForm = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
      clubCode: "",
      name: "",
      phoneNumber: "",
      passcode: "",
      signupAnswer: "",
    },
  });

  const onSubmitSignUpForm = async (values: SignUpFormValues) => {
    setLoading(true);
    setError("");
    try {
      await signUp({
        ...values,
        dateOfBirth: values.dateOfBirth.toISOString().split("T")[0],
        email: emailForm.getValues("email"),
        signupAnswer: signupQuestion ? values.signupAnswer || null : undefined,
      });
      setCurrentForm("success");
    } catch (e: unknown) {
      const response = getErrorResponse<ConnectToNewClientErrorType>(e);
      let errorMessage = t("common.somethingWentWrongTryAgain");
      if (response) {
        if ("signup_answer" in response) {
          signUpForm.setError("signupAnswer", {
            type: "server",
            message: response.signup_answer[0],
          });
          setLoading(false);
          return;
        } else if ("detail" in response) {
          errorMessage = response.detail;
        } else if ("user" in response) {
          errorMessage = response.user;
        } else if ("email" in response) {
          errorMessage = response.email;
        }
      }
      setError(errorMessage);
    }
    setLoading(false);
  };

  const SignUpFrom = (
    <Form {...signUpForm}>
      <form onSubmit={signUpForm.handleSubmit(onSubmitSignUpForm)}>
        <div className="grid gap-6">
          <FormField
            control={signUpForm.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="name">{t("form.fullName")}</FormLabel>
                <FormControl>
                  <Input
                    id="name"
                    type="string"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setError(undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="phoneNumber">
                  {t("form.phoneNumber")}
                </FormLabel>
                <FormControl>
                  <Input
                    id="phoneNumber"
                    type="phone"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setError(undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="dateOfBirth"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("form.dateOfBirth")}</FormLabel>
                <FormControl>
                  {isDesktop ? (
                    <EnhancedDatePicker
                      className={cn(
                        signUpForm.formState.errors.dateOfBirth &&
                          "border-destructive",
                      )}
                      date={field.value}
                      onDateChange={field.onChange}
                      disabled={signUpForm.formState.isSubmitting}
                      placeholder={t("form.selectBirthday")}
                    />
                  ) : (
                    <WheelDatePicker
                      className={cn(
                        signUpForm.formState.errors.dateOfBirth &&
                          "border-destructive",
                      )}
                      date={field.value}
                      onDateChange={field.onChange}
                      placeholder={t("form.selectBirthday")}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="password">{t("form.password")}</FormLabel>
                <FormControl>
                  <PasswordInput
                    id="password"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setError(undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="confirmPassword">
                  {t("form.confirmPassword")}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    id="confirmPassword"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setError(undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="passcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="passcode">
                  {t("auth.signUp.membershipId")}
                </FormLabel>
                <FormControl>
                  <Input
                    id="passcode"
                    type="string"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setError(undefined);
                    }}
                  />
                </FormControl>
                <FormDescription>
                  {t("auth.signUp.membershipIdDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {signupQuestion && (
            <FormField
              control={signUpForm.control}
              name="signupAnswer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {signupQuestion.label}
                    {signupQuestion.required && " *"}
                  </FormLabel>
                  <SignupQuestionSelect
                    question={signupQuestion}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setError(undefined);
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {/* README: This field was in previous project but it ws always disabled in configs
               if you need to use this please add a flag to client api and then use it here to show or hide this field*/}
          {/*<FormField*/}
          {/*  control={signUpForm.control}*/}
          {/*  name="clubCode"*/}
          {/*  render={({ field }) => (*/}
          {/*    <FormItem>*/}
          {/*      <FormLabel htmlFor="clubCode">Club ID</FormLabel>*/}
          {/*      <FormControl>*/}
          {/*        <Input*/}
          {/*          id="clubCode"*/}
          {/*          type="string"*/}
          {/*          {...field}*/}
          {/*          onChange={(e) => {*/}
          {/*            field.onChange(e);*/}
          {/*            setError(undefined);*/}
          {/*          }}*/}
          {/*        />*/}
          {/*      </FormControl>*/}
          {/*      <FormDescription>*/}
          {/*        If you have a club ID please enter it here.*/}
          {/*      </FormDescription>*/}
          {/*      <FormMessage />*/}
          {/*    </FormItem>*/}
          {/*  )}*/}
          {/*/>*/}
          <Button type="submit" className="w-full" disabled={loading}>
            {t("auth.signUp.createAccount")}{" "}
            {loading && <LoaderCircle className="animate-spin" />}
          </Button>
        </div>
      </form>
    </Form>
  );

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.signUp.heading")}</CardTitle>
          <CardDescription>
            {currentForm === "quiz" && t("auth.signUp.quizDescription")}
            {currentForm === "email" && t("auth.signUp.emailDescription")}
            {currentForm === "connect" &&
              t("auth.signUp.connectDescription", {
                email: emailForm.getValues("email"),
                client: client?.name,
              })}
            {currentForm === "signUp" && t("auth.signUp.signUpDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("auth.signUp.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {currentForm === "quiz" && (
            <QuizForm
              onSuccess={() => setCurrentForm("email")}
              setError={setError}
            />
          )}
          {currentForm === "email" && EmailFrom}
          {currentForm === "connect" && ConnectFrom}
          {currentForm === "signUp" && SignUpFrom}
          {currentForm === "success" && (
            <div>
              <p>{t("auth.signUp.success")}</p>
              <Link to="/sign-in">
                <Button className="mt-4 w-full" disabled={loading}>
                  {t("auth.signUp.goToSignIn")}
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
        <Trans
          i18nKey="auth.signUp.agreement"
          components={{
            terms: <Link to="/terms-of-service" />,
            privacy: <Link to="/privacy-policy" />,
          }}
        />
      </div>
    </div>
  );
};

export default Signup;
